import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { AmpelBadge, Kachel, Abschnitt, FazitBox, LeerHinweis, farben } from 'core/components/ui';
import { formatKurs, formatZahl } from 'core/utils/format';

const achseStil = { fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fill: farben.inkMuted };

export default function DividendeAnalyse({ dividende, currency }) {
  if (!dividende) {
    return <LeerHinweis>Keine Dividendendaten vorhanden.</LeerHinweis>;
  }

  const reihe = dividende.reihe || [];

  if (!dividende.zahltDividende) {
    return (
      <div>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-paper-soft p-4">
          <div className="font-serif text-xl font-semibold text-ink">Keine laufende Dividende</div>
          <AmpelBadge ampel={dividende.ampel} size="lg" />
        </div>
        <FazitBox>{dividende.fazit}</FazitBox>
      </div>
    );
  }

  return (
    <div>
      {/* Kennzahlen */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kachel
          label="Aktuelle Rendite"
          value={dividende.aktuelleRendite != null ? `${formatZahl(dividende.aktuelleRendite, 2)} %` : '–'}
          tone="gain"
        />
        <Kachel
          label="Jahre in Folge"
          value={dividende.jahreInFolge != null ? dividende.jahreInFolge : '–'}
          hint="ununterbrochen gezahlt"
        />
        <Kachel
          label="Wachstum p.a."
          value={dividende.cagr != null ? `${dividende.cagr >= 0 ? '+' : ''}${formatZahl(dividende.cagr, 1)} %` : '–'}
          tone={dividende.cagr != null && dividende.cagr < 0 ? 'loss' : 'gain'}
        />
        <Kachel label="Trend" value={dividende.trend} />
      </div>

      {/* Verlauf */}
      <Abschnitt titel="Dividende je Aktie & Rendite">
        <div className="rounded-xl border border-line bg-paper-soft p-2">
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={reihe} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid stroke={farben.line} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="jahr" tick={achseStil} stroke={farben.line} />
              <YAxis
                yAxisId="geld"
                tick={achseStil}
                width={52}
                stroke={farben.line}
                tickFormatter={(v) => formatZahl(v, 2)}
              />
              <YAxis
                yAxisId="proz"
                orientation="right"
                tick={achseStil}
                width={44}
                stroke={farben.line}
                tickFormatter={(v) => `${formatZahl(v, 0)}%`}
              />
              <Tooltip
                formatter={(v, name) =>
                  name === 'Rendite'
                    ? [`${formatZahl(v, 2)} %`, name]
                    : [formatKurs(v, currency), name]
                }
              />
              <Bar yAxisId="geld" dataKey="dividende" name="Dividende" fill={farben.brand} fillOpacity={0.45} maxBarSize={28} />
              <Line yAxisId="proz" type="monotone" dataKey="rendite" name="Rendite" stroke={farben.gain} strokeWidth={1.6} dot={{ r: 2 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Abschnitt>

      <FazitBox>{dividende.fazit}</FazitBox>
    </div>
  );
}
